import { townColorHex as hex, type TownPropPalette } from './townPropTemplates';
import { drawMasonryBlocks, drawRopeSpan, drawTimberBoards } from './propDrawingPrimitives';

export type BoundaryPropTemplate = 'timber_fence_panel' | 'timber_fence_gate' | 'stone_wall_segment' | 'stone_pier_post';

function timberPost(x: number, top: number, bottom: number, p: TownPropPalette): string {
  return `<g data-part="timber-post">
    ${drawTimberBoards({ x, y: top + 7, width: 14, height: bottom - top - 7 }, p, { axis: 'vertical', boards: 1 })}
    <path d="M${x - 2} ${top + 7}L${x + 7} ${top}L${x + 16} ${top + 7}V${top + 11}H${x - 2}Z" fill="${hex(p.primary)}" />
    <path d="M${x} ${top + 8}H${x + 14}" stroke="${hex(p.woodEdge)}" stroke-width="1.4" />
    <path d="M${x} ${bottom - 7}H${x + 14}V${bottom}H${x}Z" fill="${hex(p.iron)}" />
    <path d="M${x + 3} ${bottom - 6}V${bottom - 1}" stroke="${hex(p.woodEdge)}" stroke-width="1" />
  </g>`;
}

/** 144×88. Two open rails, not a filled wall or a walkable platform. */
function fence(p: TownPropPalette): string {
  return `<g data-part="fence-rails">
    ${drawTimberBoards({ x: 12, y: 27, width: 120, height: 11 }, p, { axis: 'horizontal', boards: 1 })}
    ${drawTimberBoards({ x: 12, y: 55, width: 120, height: 11 }, p, { axis: 'horizontal', boards: 1 })}
    <path d="M21 30L28 30 M64 59L77 59 M108 33L118 33" stroke="${hex(p.shade)}" stroke-width="1.1" />
  </g>
  ${timberPost(7, 7, 88, p)}${timberPost(123, 7, 88, p)}
  <g data-part="fence-lashings">
    ${[7, 123].map(x => [30, 58].map(y => drawRopeSpan({ x: x + 2, y }, { x: x + 12, y: y + 3 }, p,
    { sag: 1, thickness: 1.5 })).join('')).join('')}
    <path d="M10 37L18 29 M126 65L134 57" stroke="${hex(p.woodEdge)}" stroke-width="1.1" />
  </g>`;
}

/** 128×104. Raised slats, a structural diagonal and strap hinges identify the gate. */
function gate(p: TownPropPalette): string {
  const slats = [24, 37, 50, 63, 76, 89].map((x, index) => {
    const top = [27, 20, 16, 16, 20, 27][index];
    return `<g data-part="gate-slat">
      ${drawTimberBoards({ x, y: top + 5, width: 9, height: 88 - top - 5 }, p, { axis: 'vertical', boards: 1 })}
      <path d="M${x} ${top + 5}L${x + 4.5} ${top}L${x + 9} ${top + 5}Z" fill="${hex(p.woodEdge)}" />
    </g>`;
  }).join('');
  return `<g data-part="gate-leaf">
    ${slats}
    ${drawTimberBoards({ x: 22, y: 35, width: 79, height: 8 }, p, { axis: 'horizontal', boards: 1 })}
    ${drawTimberBoards({ x: 22, y: 81, width: 79, height: 8 }, p, { axis: 'horizontal', boards: 1 })}
    <path data-part="gate-brace" d="M25 81L94 42H100L31 85H25Z" fill="${hex(p.woodEdge)}" />
    <path d="M32 79L91 45" stroke="${hex(p.wood)}" stroke-width="1.4" />
  </g>
  ${timberPost(7, 9, 104, p)}${timberPost(107, 9, 104, p)}
  <g data-part="gate-hinges" fill="${hex(p.iron)}">
    ${[39, 85].map(y => `<g data-part="strap-hinge"><path d="M14 ${y - 4}H39L45 ${y}L39 ${y + 4}H14Z" />
      <rect x="15" y="${y - 7}" width="5" height="14" rx="1" />
      <circle cx="27" cy="${y}" r="1.2" fill="${hex(p.stone)}" />
      <circle cx="37" cy="${y}" r="1" fill="${hex(p.stone)}" /></g>`).join('')}
  </g>
  <g data-part="gate-latch">
    <path d="M91 52H116V57H91Z M113 49H119V61H113Z" fill="${hex(p.iron)}" />
    <circle cx="96" cy="54.5" r="1.3" fill="${hex(p.stone)}" />
    ${drawRopeSpan({ x: 98, y: 57 }, { x: 103, y: 58 }, p, { sag: 7, thickness: 1.6 })}
  </g>`;
}

/** 144×72. Uneven coping and offset joints keep the low wall dry-laid. */
function wall(p: TownPropPalette): string {
  const coping = [
    'M3 27L6 16L28 14L35 20L33 29Z',
    'M34 28L37 13L63 12L68 21L65 30Z',
    'M66 29L71 17L96 14L103 24L99 31Z',
    'M100 30L105 18L130 17L140 25L137 33Z',
  ];
  return `<g data-part="dry-stone-body">
    ${drawMasonryBlocks({ x: 4, y: 27, width: 136, height: 45 }, p, { courses: 3, columns: 5 })}
    <path d="M4 63L9 60L11 70H30V72H4Z M119 72V68L132 64L140 67V72Z" fill="${hex(p.shade)}" />
  </g>
  <g data-part="wall-coping" fill="${hex(p.stone)}" stroke="${hex(p.shade)}" stroke-width="1.5" stroke-linejoin="round">
    ${coping.map(d => `<path d="${d}" />`).join('')}
  </g>
  <path d="M9 18L26 16 M41 16L60 15 M75 19L94 17 M110 21L128 20" fill="none" stroke="${hex(p.woodEdge)}" stroke-width="1.3" />
  <g data-part="wall-weathering" fill="${hex(p.primary)}" opacity="0.72">
    <path d="M7 22L12 20L19 23L22 28H10Z M75 20L81 19L86 23L85 28L79 26Z M114 45L123 44L126 48L118 49Z" />
  </g>`;
}

/** 56×100. A stepped cap and broad foot frame a narrow masonry shaft. */
function pier(p: TownPropPalette): string {
  return `<g data-part="pier-shaft">
    ${drawMasonryBlocks({ x: 10, y: 29, width: 36, height: 65 }, p, { courses: 4, columns: 2 })}
    <path d="M40 29H46V94H40Z" fill="${hex(p.shade)}" opacity="0.45" />
  </g>
  <g data-part="pier-cap">
    <path d="M3 17L10 10H46L53 17V24H3Z" fill="${hex(p.stone)}" />
    <path d="M3 24H53L47 30H9Z" fill="${hex(p.shade)}" />
    <path d="M4 18H52 M11 12H45" stroke="${hex(p.woodEdge)}" stroke-width="1.4" />
    <path d="M12 12H43L39 16H15Z" fill="${hex(p.primary)}" opacity="0.7" />
  </g>
  <g data-part="pier-foot">
    <path d="M9 89H47L53 94V100H3V94Z" fill="${hex(p.stone)}" />
    <path d="M3 96H53V100H3Z" fill="${hex(p.shade)}" />
    <path d="M9 91H46 M5 94H51" stroke="${hex(p.woodEdge)}" stroke-width="1.3" />
    <path d="M22 96V100 M39 96V100" stroke="${hex(p.iron)}" stroke-width="1" />
  </g>`;
}

/** Static decorative bodies only. These drawings confer no collision or interaction. */
export function buildBoundaryPropBody(template: BoundaryPropTemplate, palette: TownPropPalette): string {
  switch (template) {
  case 'timber_fence_panel': return fence(palette);
  case 'timber_fence_gate': return gate(palette);
  case 'stone_wall_segment': return wall(palette);
  case 'stone_pier_post': return pier(palette);
  }
}
